<template>
	<div class="page script-scroll-page">
		<Navigation pageName="script" :data="script ? { scriptID: script.id, scriptName: script.name } : {}" />

		<div v-if="loading" class="p-5 text-center">
			<span class="mif-spinner4 mif-ani-spin mif-4x"></span>
		</div>

		<div v-else-if="script" class="script-page p-5">
			<div class="mb-4 text-right">
				<button v-if="!isNew" class="button alert" style="float:left" @click="deleteScript">{{ $t("Delete") }}</button>
				<router-link class="button mr-2" to="/scripts/inventory">{{ $t("Close") }}</router-link>
				<button class="button secondary mr-2" :disabled="!canDeploy" :title="deployButtonTitle" @click="deployScript">
					{{ $t('Deploy') }}
				</button>
				<button class="button secondary" :disabled="saving || !enableSave" v-disable-tooltip:saveButtonDisabled_message @click="save">
					{{ isNew ? $t('Create') : $t("Save") }}
				</button>
			</div>

			<ClientMessageView ref="script_message" :commands="['scriptmethods']" />

			<div class="script-form">
				<section class="script-section">
					<div class="script-section-header">
						<h5 class="m-0 text-bold">{{ $t('Insert a script') }}</h5>
					</div>
					<div class="script-section-body">
						<div class="row mb-4">
							<div class="cell-md-6">
								<label>{{ $t("OS") }}</label>
								<select class="select text-small w-100 px-2" v-model="script.os" :disabled="!isNew">
									<option v-for="(os, id) in ScriptsHelper.osValues" :key="id" :value="id">{{ os }}</option>
								</select>
							</div>
							<div class="cell-md-6">
								<label>{{ $t("Language") }}</label>
								<select class="select text-small w-100 px-2" v-model="script.language" :disabled="!isNew">
									<option v-for="(language, id) in availableLanguages" :key="id" :value="id">{{ language }}</option>
								</select>
							</div>
						</div>
						<label>{{ $t("Script") }}*</label>
						<textarea ref="scriptEditor" v-model="script.content" class="script-terminal" :class="scriptTerminalClass" spellcheck="false" @keydown.tab.prevent="insertScriptTab"></textarea>
					</div>
				</section>

				<section class="script-section">
					<div class="script-section-header d-flex flex-align-center">
						<h5 class="m-0 text-bold">{{ $t('Arguments') }} ({{ $t('Optional') }})</h5>
						<span :key="'arguments-hint-' + script.language" class="credential-help-icon ml-2" data-role="hint" data-hint-position="bottom" data-cls-hint="credential-help-hint" data-hint-hide="30000" :data-hint-text="argumentsHelpHint">?</span>
					</div>
					<div class="script-section-body">
						<div v-for="(arg, index) in script.args" :key="'script-arg-' + index" class="d-flex flex-align-end mb-2">
							<div v-if="script.language === 'powershell'" class="argument-field mr-2">
								<label v-if="index === 0" class="text-bold">{{ $t("Name") }}</label>
								<input class="input" :class="{ invalid: containsDoubleQuotes(arg.name) }" v-model.trim="arg.name" type="text" @input="sanitizeArgumentField(arg, 'name', $event)" />
							</div>
							<div class="argument-field mr-2">
								<label v-if="index === 0" class="text-bold">{{ $t("Value") }}</label>
								<input class="input" :class="{ invalid: containsDoubleQuotes(arg.value) }" v-model.trim="arg.value" type="text" @input="sanitizeArgumentField(arg, 'value', $event)" />
							</div>
							<button class="button alert square" type="button" @click="removeScriptArg(index)"><span class="mif-bin"></span></button>
						</div>
						<div class="d-flex flex-align-end">
							<div v-if="script.language === 'powershell'" class="argument-field mr-2">
								<label v-if="script.args.length === 0" class="text-bold">{{ $t("Name") }}</label>
								<input class="input" v-model.trim="newScriptArg.name" :placeholder="$t('Name')" type="text" @input="sanitizeArgumentField(newScriptArg, 'name', $event)" @keyup.enter="addScriptArg" />
							</div>
							<div class="argument-field mr-2">
								<label v-if="script.args.length === 0" class="text-bold">{{ $t("Value") }}</label>
								<input class="input" v-model.trim="newScriptArg.value" :placeholder="script.language === 'powershell' ? $t('Value') : $t('Add argument')" type="text" @input="sanitizeArgumentField(newScriptArg, 'value', $event)" @keyup.enter="addScriptArg" />
							</div>
							<button class="button secondary square" type="button" :disabled="!canAddScriptArg" :title="$t('Add')" @click="addScriptArg"><span class="mif-add mif-2x"></span></button>
						</div>
					</div>
				</section>

				<section class="script-section">
					<div class="script-section-header d-flex flex-align-center">
						<h5 class="m-0 text-bold">{{ $t('Credentials') }} ({{ $t('Optional') }})</h5>
						<span :key="'credentials-hint-' + script.language" class="credential-help-icon ml-2" data-role="hint" data-hint-position="bottom" data-cls-hint="credential-help-hint" data-hint-hide="30000" :data-hint-text="credentialsHelpHint">?</span>
					</div>
					<div class="script-section-body">
						<div v-for="(credential, index) in script.creds" :key="'script-credential-' + index" class="d-flex flex-align-end mb-2">
							<div class="credential-field mr-2"><label v-if="index === 0" class="text-bold">{{ $t("Credentials name") }}<span class="mif-info_outline credential-name-help-icon ml-1" data-role="hint" data-hint-position="top" data-cls-hint="credential-name-help-hint" :data-hint-text="$t('credentialNameRequirements')"></span></label>
								<div class="credential-readonly">{{ credential.name }}</div>
							</div>
							<div class="credential-field mr-2"><label v-if="index === 0" class="text-bold">{{ $t("User") }} / {{ $t("Name") }}</label>
								<div class="credential-readonly">{{ credential.username }}</div>
							</div>
							<div class="credential-field mr-2"><label v-if="index === 0" class="text-bold">{{ $t("Password") }} / {{ $t("Secret") }}</label>
								<div class="credential-readonly credential-password">••••••••</div>
							</div>
							<button class="button alert square" type="button" @click="removeScriptCredential(index)"><span class="mif-bin"></span></button>
						</div>
						<div class="d-flex flex-align-end">
							<div class="credential-field mr-2">
								<label v-if="script.creds.length === 0" class="text-bold">{{ $t("Credentials name") }}<span class="mif-info credential-name-help-icon ml-1" data-role="hint" data-hint-position="top" data-cls-hint="credential-name-help-hint" :data-hint-text="$t('credentialNameRequirements')"></span></label>
								<input class="input" :class="{ 'invalid': credentialNameError }" v-model="newCredential.name" type="text" maxlength="32" autocomplete="off" @keyup.enter="addScriptCredential" />
								<small v-if="credentialNameError" class="fg-red d-block mt-1">{{ credentialNameError }}</small>
							</div>
							<div class="credential-field mr-2"><label v-if="script.creds.length === 0" class="text-bold">{{ $t("User") }} / {{ $t("Name") }}</label><input class="input" v-model.trim="newCredential.username" type="text" @keyup.enter="addScriptCredential" /></div>
							<div class="credential-field mr-2"><label v-if="script.creds.length === 0" class="text-bold">{{ $t("Password") }} / {{ $t("Secret") }}</label><input class="input" type="password" v-model.trim="newCredential.password" autocomplete="new-password" @keyup.enter="addScriptCredential" /></div>
							<button class="button secondary square" type="button" :disabled="!canAddScriptCredential" :title="$t('Add')" @click="addScriptCredential"><span class="mif-add mif-2x"></span></button>
						</div>
					</div>
				</section>



				<section class="script-section">
					<div class="script-section-header">
						<h5 class="m-0 text-bold">{{ $t('Script informations') }}</h5>
					</div>
					<div class="script-section-body">
						<div class="form-group"><label>{{ $t("Name") }}*</label><input class="input w-100" required v-model.trim="script.name" type="text" /></div>
						<div class="form-group mt-2"><label>{{ $t("Description") }} ({{ $t('Optional') }})</label><input maxlength="255" class="input w-100" v-model="script.description" type="text" /></div>
						<div class="form-group mt-2"><label>{{ $t("Status") }}</label><select class="select text-small w-auto px-2" v-model="script.status">
								<option v-for="(status, id) in ScriptsHelper.statusValues" :key="id" :value="id">{{ $t(status) }}</option>
							</select></div>
						<div class="form-group mt-2"><label>{{ $t("Tags") }} ({{ $t('Optional') }})</label>
							<div v-for="(tag, index) in script.tags" :key="'script-tag-' + index" class="d-flex flex-align-center mb-2"><input class="input mr-2" v-model.trim="script.tags[index]" type="text" /><button class="button alert square" type="button" @click="removeScriptTag(index)"><span class="mif-bin"></span></button></div>
							<div class="d-flex flex-align-center"><input class="input mr-2" v-model.trim="newScriptTag" :placeholder="$t('Add tag')" type="text" @keyup.enter="addScriptTag" /><button class="button secondary" type="button" :disabled="!canAddScriptTag" @click="addScriptTag">{{ $t("Add") }}</button></div>
						</div>
					</div>
				</section>
			</div>

			<div class="my-4 text-right">
				<router-link class="button mr-2" to="/scripts/inventory">{{ $t("Close") }}</router-link>
				<button class="button secondary mr-2" :disabled="!canDeploy" :title="deployButtonTitle" @click="deployScript">
					{{ $t('Deploy') }}
				</button>
				<button class="button secondary" :disabled="saving || !enableSave" @click="save">{{ isNew ? $t('Create') : $t("Save") }}</button>
			</div>
		</div>
	</div>
</template>

<script>
import Navigation from './utils/Navigation';
import ClientMessageView from './dialogs/view/ClientMessageView.vue';
import { ClientMessage, ScriptsHelper } from '../utils/objects';

export default {
	name: 'Script',
	components: { Navigation, ClientMessageView },
	props: { idScript: { type: String, default: null } },
	data() {
		return {
			ScriptsHelper,
			script: null,
			originalScriptSnapshot: '',
			loading: true,
			saving: false,
			saveButtonDisabled_message: '',
			newScriptArg: { name: '', value: '' },
			newScriptTag: '',
			newCredential: { name: '', username: '', password: '' },
			touchedCredentialPasswords: {}
		};
	},
	computed: {
		enableSave() {
			if (!this.script || !this.script.content) {
				this.saveButtonDisabled_message = this.$t('Insert a script');
				return false;
			}
			if (!this.script.name) {
				this.saveButtonDisabled_message = this.$t('Enter a name');
				return false;
			}
			if (!this.scriptCredentialsAreValid) {
				this.saveButtonDisabled_message = this.$t('Credential names are invalid or duplicated');
				return false;
			}
			if (!this.scriptArgumentsAreValid) {
				this.saveButtonDisabled_message = this.$t('Double quotes are not allowed in arguments');
				return false;
			}
			this.saveButtonDisabled_message = '';
			return this.isNew || this.hasPendingChanges;
		},
		isNew() { return !this.script || this.script.id == null; },
		hasPendingChanges() {
			if (!this.script || !this.originalScriptSnapshot) return true;
			const hasDraftValues = Object.values(this.newScriptArg).some(value => value.trim() !== '') || this.newScriptTag.trim() !== ''
				|| Object.values(this.newCredential).some(value => value.trim() !== '');
			return hasDraftValues || this.scriptSnapshot(this.script) !== this.originalScriptSnapshot;
		},
		canDeploy() {
			return !this.isNew && !this.saving && !this.hasPendingChanges && this.script.status !== 'draft';
		},
		deployButtonTitle() {
			if (this.isNew) return this.$t('Save');
			if (this.hasPendingChanges) return this.$t('Save');
			if (this.script.status === 'draft') return this.$t('Draft scripts cannot be deployed');
			return this.$t('Deploy');
		},
		canAddScriptArg() {
			if (!this.script || this.newScriptArg.value.trim() === '') return false;
			if (this.containsDoubleQuotes(this.newScriptArg.name) || this.containsDoubleQuotes(this.newScriptArg.value)) return false;
			return this.script.language !== 'powershell' || this.newScriptArg.name.trim() !== '';
		},
		scriptArgumentsAreValid() {
			if (!this.script || !Array.isArray(this.script.args)) return true;
			return this.script.args.every(arg => !this.containsDoubleQuotes(arg.name) && !this.containsDoubleQuotes(arg.value));
		},
		canAddScriptTag() { return this.script && this.canAddListItem(this.script.tags, this.newScriptTag); },
		credentialNameError() {
			const name = this.newCredential.name;
			if (name === '') return '';
			if (!this.isValidCredentialName(name)) {
				return this.$t('Use 1-32 characters: A-Z, a-z, 0-9 or underscore. The name cannot start with a number');
			}
			if (this.isDuplicateCredentialName(name)) {
				return this.$t('Credential names must be unique, ignoring uppercase and lowercase');
			}
			return '';
		},
		scriptCredentialsAreValid() {
			if (!this.script || !Array.isArray(this.script.creds)) return true;
			const names = this.script.creds.map(credential => credential.name);
			return names.every(name => this.isValidCredentialName(name))
				&& new Set(names.map(name => name.toLowerCase())).size === names.length;
		},
		canAddScriptCredential() {
			return this.newCredential.name !== '' && !this.credentialNameError
				&& this.newCredential.username.trim() !== '' && this.newCredential.password.trim() !== '';
		},
		hasCompleteCredentialDraft() { return this.canAddScriptCredential; },
		isPowershellScript() { return !!this.script && this.script.language === 'powershell'; },
		argumentsHelpHint() {
			if (this.isPowershellScript) {
				return `<b>Powershell</b><br><br>`
					+ `${this.$t('argumentsHint_powershell_1')} <i>-${this.$t('Name')} ${this.$t('Value')}</i><br><br>`
					+ `${this.$t('argumentsHint_powershell_2')} :<br><br>`
					+ `<i>param(</i><br><i>&nbsp;&nbsp;&nbsp;&nbsp;$${this.$t('Name')}1,</i><br><i>&nbsp;&nbsp;&nbsp;&nbsp;$${this.$t('Name')}2</i><br><i>)</i><br><br>`
					+ `<i>${this.$t('Name')}1</i> &rarr; <i>-${this.$t('Name')}1 ${this.$t('Value')}1</i><br><br>`
					+ `&bull; ${this.$t('argumentsHint_powershell_3')}<br>`
					+ `&bull; ${this.$t('argumentsHint_powershell_4')}<br>`
					+ `&bull; ${this.$t('argumentsHint_powershell_5')}`;
			}
			return `<b>CMD</b><br><br>`
				+ `${this.$t('argumentsHint_cmd_1')} :<br><br>`
				+ `<i>set "${this.$t('Value')}1=%~1"</i><br><i>set "${this.$t('Value')}2=%~2"</i><br><br>`
				+ `${this.$t('argumentsHint_cmd_2')}`;
		},
		credentialsHelpHint() {
			const usage = this.isPowershellScript
				? '<i>$env:CREDENTIAL_NAME_username</i><br><i>$env:CREDENTIAL_NAME_password</i>'
				: '<i>%CREDENTIAL_NAME_username%</i><br><i>%CREDENTIAL_NAME_password%</i>';
			const example = this.isPowershellScript
				? '<i>$username = $env:MyCredential_username</i><br><i>$password = $env:MyCredential_password</i>'
				: '<i>set "username=%MyCredential_username%"</i><br><i>set "password=%MyCredential_password%"</i>';
			return `<b>${this.isPowershellScript ? 'Powershell' : 'CMD'}</b><br><br>`
				+ `${this.$t('useCredentialsInScript_1')} :<br><br>${usage}<br><br>`
				+ `${this.$t('useCredentialsInScript_2')} <i>MyCredential</i> :<br><br>${example}`;
		},
		availableLanguages() {
			if (!this.script) return {};
			const languagesByOS = { windows: ['powershell', 'cmd'], linux: ['bash'], mac: ['bash'] };
			return (languagesByOS[this.script.os] || []).reduce((languages, id) => {
				languages[id] = this.ScriptsHelper.languagesValues[id];
				return languages;
			}, {});
		},
		scriptTerminalClass() { return this.script && this.script.language === 'powershell' ? 'powershell-terminal' : 'bash-terminal'; }
	},
	watch: {
		'script.os': function () {
			if (!this.script) return;
			const languageIDs = Object.keys(this.availableLanguages);
			if (!languageIDs.includes(this.script.language)) this.script.language = languageIDs[0] || '';
		},
		'script.language': function () {
			if (!this.script || !this.isNew) return;
			this.normalizeScriptArgs();
		}
	},
	async created() {
		if (!this.$root.checkAuth()) return;
		this.$session.setRoomPC(null);
		this.$root.socket('SETPC', null);
		this.script = this.idScript ? await this.$api.getScript(this.idScript) : ScriptsHelper.get();
		if (!this.script) {
			this.$router.replace('/scripts/inventory');
			return;
		}
		this.initScriptCollections();
		this.originalScriptSnapshot = this.scriptSnapshot(this.script);
		this.loading = false;
	},
	methods: {
		containsDoubleQuotes(value) {
			return typeof value === 'string' && value.includes('"');
		},
		sanitizeArgumentField(argument, field, event) {
			const sanitizedValue=event.target.value.replace(/"/g, '');
			event.target.value=sanitizedValue;
			this.$set(argument, field, sanitizedValue);
		},
		isValidCredentialName(name) {
			return typeof name === 'string' && /^[A-Za-z_][A-Za-z0-9_]{0,31}$/.test(name);
		},
		isDuplicateCredentialName(name) {
			if (!this.script || !Array.isArray(this.script.creds)) return false;
			const normalizedName = name.toLowerCase();
			return this.script.creds.some(credential => typeof credential.name === 'string'
				&& credential.name.toLowerCase() === normalizedName);
		},
		scriptSnapshot(script) {
			return JSON.stringify(script);
		},
		initScriptCollections() {
			const credential = this.script.creds;
			if (!Array.isArray(credential)) this.$set(this.script, 'creds', credential && (credential.username || credential.password) ? [credential] : []);
			this.normalizeScriptArgs();
			if (!Array.isArray(this.script.tags)) this.$set(this.script, 'tags', []);
			const languageIDs = Object.keys(this.availableLanguages);
			if (!languageIDs.includes(this.script.language)) this.script.language = languageIDs[0] || '';
		},
		normalizeScriptArgs() {
			const args = Array.isArray(this.script.args) ? this.script.args : [];
			this.$set(this.script, 'args', args.map((arg, index) => ({
				name: this.script.language === 'cmd'
					? `arg${index}`
					: String(arg && typeof arg === 'object' ? arg.name || '' : ''),
				value: String(arg && typeof arg === 'object' ? arg.value || '' : arg || '')
			})));
		},
		addScriptCredential() {
			if (!this.canAddScriptCredential) return;
			this.script.creds.push({ ...this.newCredential, name: this.newCredential.name.trim() });
			this.$set(this.touchedCredentialPasswords, this.script.creds.length - 1, true);
			this.newCredential = { name: '', username: '', password: '' };
		},
		removeScriptCredential(index) { this.script.creds.splice(index, 1); },
		canAddListItem(list, value) { return Array.isArray(list) && value.trim() !== '' && list.every(item => item != null && item.toString().trim() !== ''); },
		addScriptArg() {
			if (!this.canAddScriptArg) return;
			this.script.args.push({
				name: this.script.language === 'cmd' ? `arg${this.script.args.length}` : this.newScriptArg.name.trim(),
				value: this.newScriptArg.value.trim()
			});
			this.newScriptArg = { name: '', value: '' };
		},
		removeScriptArg(index) {
			this.script.args.splice(index, 1);
			if (this.script.language === 'cmd') this.normalizeScriptArgs();
		},
		addScriptTag() { if (this.canAddScriptTag) { this.script.tags.push(this.newScriptTag.trim()); this.newScriptTag = ''; } },
		removeScriptTag(index) { this.script.tags.splice(index, 1); },
		insertScriptTab() {
			const editor = this.$refs.scriptEditor;
			if (!editor) return;
			const start = editor.selectionStart;
			this.script.content = (this.script.content || '').slice(0, start) + '\t' + (this.script.content || '').slice(editor.selectionEnd);
			this.$nextTick(() => { editor.selectionStart = editor.selectionEnd = start + 1; });
		},
		async save() {
			if (!this.enableSave || this.saving) return;
			if (this.hasCompleteCredentialDraft) this.addScriptCredential();
			const wasNew = this.isNew;
			this.saving = true;
			const saved = wasNew ? await this.$api.createScript(this.script) : await this.$api.editScript(this.script);
			this.saving = false;
			if (!saved) return;

			if (wasNew) {
				const scripts = await this.$api.getScripts();
				const createdScript = scripts
					.filter(script => script.name === this.script.name)
					.sort((first, second) => new Date(second.inserted_at || second.updated_at || 0) - new Date(first.inserted_at || first.updated_at || 0))[0];
				if (createdScript && createdScript.id != null) {
					this.script = await this.$api.getScript(createdScript.id) || createdScript;
					this.initScriptCollections();
					this.$router.replace({ name: 'script', params: { idScript: String(createdScript.id) } });
				}
			}

			this.originalScriptSnapshot = this.scriptSnapshot(this.script);
			this.$root.$emit('refreshScripts');
		},
		deployScript() {
			if (!this.canDeploy) return;
			this.$root.$emit('OPENSCRIPTDEPLOYDIALOG', this.script.id);
		},
		deleteScript() {
			const dialogID = Math.floor(100000 + Math.random() * 900000).toString();
			this.$root.$on(dialogID, async ok => {
				this.$root.$off(dialogID);
				if (!ok) return;
				if (!await this.$api.deleteScript(this.script.id, this.script.name)) return;
				this.$root.$emit('refreshScripts');
				this.$router.push('/scripts/inventory');
			});
			this.$root.openDialogActions(dialogID, this.$t('Delete Script'), this.$t('deleteConfirmation', { 0: this.script.name }), this.$t('Yes'), 'mr-2 secondary', true, this.$t('No'), 'alert', false);
		},
		setClientMessage(clientMessage) { if (this.$refs.script_message) this.$refs.script_message.setClientMessage(clientMessage); },
		showValidationMessage() { if (!this.enableSave) this.setClientMessage(ClientMessage.info_translated(this.saveButtonDisabled_message)); }
	}
};
</script>

<style scoped>
.script-scroll-page {
	height: 100vh;
	overflow-y: auto;
}

.script-page {
	max-width: 1280px;
	margin: 0 auto;
	padding-bottom: 48px !important;
}

.script-form {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.script-section {
	border: 1px solid #d9d9d9;
	background: #fff;
}

.script-section-header {
	padding: 12px 16px;
	border-bottom: 1px solid #d9d9d9;
	background: #f5f5f5;
}

.script-section-body {
	padding: 20px;
}

.script-terminal {
	display: block;
	width: 100%;
	height: 48vh;
	min-height: 320px;
	padding: 10px 12px;
	border: 1px solid #2b3440;
	resize: vertical;
	overflow: auto;
	white-space: pre;
	tab-size: 4;
	font-family: "Cascadia Mono", "Consolas", "Courier New", monospace;
	font-size: 13px;
	line-height: 1.25;
	outline: none;
}

.powershell-terminal {
	background: #012456;
	color: #f5f9ff;
	caret-color: #f5f9ff;
	border-color: #315f99;
}

.bash-terminal {
	background: #111;
	color: #f2f2f2;
	caret-color: #f2f2f2;
	border-color: #333;
}

.powershell-terminal::selection {
	background: #315f99;
}

.bash-terminal::selection {
	background: #3f3f3f;
}



.argument-field,
.credential-field {
	flex: 1 1 0;
	min-width: 0;
}

.credential-name-help-icon {
	color: black;
	font-size: 18px;
	vertical-align: middle;
	cursor: help;
}

.credential-readonly {
	min-height: 36px;
	padding: 8px 12px;
	border: 1px solid #d9d9d9;
	background: #f2f2f2;
	color: #555;
	line-height: 18px;
	word-break: break-word;
}

.credential-password {
	letter-spacing: 2px;
}
</style>
